import { useState } from "react";
import Cropper from "react-easy-crop";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Crop, Loader2 } from "lucide-react";

const cropToFile = (url, area, file) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(area.width);
    canvas.height = Math.round(area.height);
    canvas.getContext("2d").drawImage(img, area.x, area.y, area.width, area.height, 0, 0, canvas.width, canvas.height);
    const type = file.type === "image/png" ? "image/png" : "image/jpeg";
    canvas.toBlob((b) => (b ? resolve(new File([b], file.name, { type })) : reject(new Error("crop"))), type, 0.92);
  };
  img.onerror = reject;
  img.src = url;
});

export const CropDialog = ({ file, url, onClose, onCropped }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);
  const [busy, setBusy] = useState(false);

  const apply = async () => {
    setBusy(true);
    try {
      onCropped(await cropToFile(url, area, file));
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="crop-dialog">
        <DialogHeader>
          <DialogTitle>Recadrer au format écran (16/9)</DialogTitle>
          <DialogDescription>Déplacez l'image et zoomez pour choisir la partie visible sur l'écran TV.</DialogDescription>
        </DialogHeader>
        <div className="relative aspect-video w-full overflow-hidden rounded-md bg-black" data-testid="crop-area">
          <Cropper image={url} crop={crop} zoom={zoom} aspect={16 / 9} minZoom={1} maxZoom={4}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={(_, px) => setArea(px)} />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">Zoom</span>
          <Slider min={1} max={4} step={0.05} value={[zoom]} onValueChange={([v]) => setZoom(v)} data-testid="crop-zoom-slider" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="crop-cancel">Annuler</Button>
          <Button onClick={apply} disabled={!area || busy} data-testid="crop-apply">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Crop className="mr-1 h-4 w-4" />} Recadrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
